import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ReactComponent as GoogleWorkspaceWordmark } from "./assets/google-workspace-wordmark.svg";
import companyLogo from "./assets/epic-logo.svg";
import agentAvatar from "./assets/engie.png";
import epicWave from "./assets/epic-wave.svg";
import "./App.css";

const EPIC_ACCENT = "#0078F2";
const EPIC_WHITE = "#FFFFFF";

function IconStopwatch() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="13" r="8" stroke={EPIC_WHITE} strokeWidth="2" />
      <path d="M12 9v5l3 2" stroke={EPIC_WHITE} strokeWidth="2" strokeLinecap="round" />
      <path d="M9 3h6" stroke={EPIC_WHITE} strokeWidth="2" strokeLinecap="round" />
      <path d="M12 3v2" stroke={EPIC_WHITE} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconVideo() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="6" width="13" height="12" rx="2" stroke={EPIC_WHITE} strokeWidth="2" />
      <path d="M16 10l5-3v10l-5-3v-4z" stroke={EPIC_WHITE} strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

function IconBrand() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <text
        x="12"
        y="18"
        textAnchor="middle"
        fontFamily="'Inter', 'Helvetica Neue', Arial, sans-serif"
        fontSize="16"
        fontWeight="700"
        fill={EPIC_ACCENT}
      >
        E
      </text>
    </svg>
  );
}


const MEETING_DURATION_MIN = 30;
/** Earliest bookable day is this many full days after today. */
const MIN_DAYS_AHEAD = 10;
/** Number of consecutive days that can be booked (starting on the first bookable day). */
const SELECTABLE_DAYS = 10;

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function formatTimezoneLabel(date) {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    const offsetMin = -date.getTimezoneOffset();
    const sign = offsetMin >= 0 ? "+" : "-";
    const ah = Math.floor(Math.abs(offsetMin) / 60);
    const am = Math.abs(offsetMin) % 60;
    const gmt = `GMT${sign}${pad2(ah)}:${pad2(am)}`;
    const city = tz.includes("/") ? tz.split("/").pop().replace(/_/g, " ") : tz;
    const longName =
      new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "long" })
        .formatToParts(date)
        .find((p) => p.type === "timeZoneName")?.value || "";
    const label = longName ? `${longName} - ${city}` : city;
    return `(${gmt}) ${label}`;
  } catch {
    return "(GMT+00:00) UTC";
  }
}

function buildMonthGrid(viewMonth) {
  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const first = new Date(year, month, 1);
  const startWeekday = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function timeSlotsForDate(date) {
  if (!date) return [];
  const slots = [];
  for (let h = 8; h <= 17; h++) {
    for (let m of [0, 30]) {
      if (h === 17 && m > 0) break;
      const label = `${h % 12 === 0 ? 12 : h % 12}:${pad2(m)}${h < 12 ? "am" : "pm"}`;
      slots.push({ h, m, label });
    }
  }
  return slots;
}

const JOB_INTERESTS = [
  "IT & Software Development",
  "Marketing Manager",
  "Design & Creative",
  "Software Developer",
  "Sales & Business Development",
  "Product Manager",
  "Customer Support & Operations",
  "Finance & Accounting",
  "HR & People Ops",
  "Other",
];

const WORK_PREFERENCES = [
  "Full-time",
  "Hybrid",
  "Onsite",
  "Remote",
  "Part-time",
  "Contract",
  "Freelance",
];

function loadPersistedBooking() {
  try {
    const sessionId = localStorage.getItem("session_id");
    if (!sessionId || localStorage.getItem(`confirmation_done_${sessionId}`) !== "1") {
      return { step: "schedule", summary: null };
    }
    const raw = localStorage.getItem(`booking_summary_${sessionId}`);
    return { step: "confirmed", summary: raw ? JSON.parse(raw) : null };
  } catch {
    return { step: "schedule", summary: null };
  }
}

export default function Confirmation() {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_BASE_URL;
  const [restore] = useState(() => loadPersistedBooking());
  const [step, setStep] = useState(() => restore.step);
  const [viewMonth, setViewMonth] = useState(() => {
    const t = startOfDay(new Date());
    const first = new Date(t);
    first.setDate(first.getDate() + MIN_DAYS_AHEAD);
    return startOfMonth(first);
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [confirmedSummary, setConfirmedSummary] = useState(() => restore.summary);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    jobInterest: "",
    workPreference: "",
  });
  const [errors, setErrors] = useState({});

  const bookableRange = useMemo(() => {
    const todayStart = startOfDay(new Date());
    const firstSelectable = new Date(todayStart);
    firstSelectable.setDate(firstSelectable.getDate() + MIN_DAYS_AHEAD);
    const maxSelectableEnd = new Date(firstSelectable);
    maxSelectableEnd.setDate(maxSelectableEnd.getDate() + SELECTABLE_DAYS - 1);
    return { firstSelectable, maxSelectableEnd };
  }, []);

  const viewMonthStart = useMemo(() => startOfMonth(viewMonth), [viewMonth]);
  const monthCells = useMemo(() => buildMonthGrid(viewMonthStart), [viewMonthStart]);
  const timeSlots = useMemo(() => timeSlotsForDate(selectedDate), [selectedDate]);

  const nextMonthStart = useMemo(
    () => new Date(viewMonthStart.getFullYear(), viewMonthStart.getMonth() + 1, 1),
    [viewMonthStart],
  );
  const canGoPrev = viewMonthStart.getTime() > startOfMonth(bookableRange.firstSelectable).getTime();
  const canGoNext = startOfDay(nextMonthStart).getTime() <= startOfDay(bookableRange.maxSelectableEnd).getTime();

  const isSelectableDay = (d) => {
    if (!d) return false;
    const ts = startOfDay(d).getTime();
    return (
      ts >= startOfDay(bookableRange.firstSelectable).getTime() &&
      ts <= startOfDay(bookableRange.maxSelectableEnd).getTime()
    );
  };

  React.useEffect(() => {
    const sessionId = localStorage.getItem("session_id");
    if (!sessionId) {
      navigate("/");
    }
  }, [navigate]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleInputFocus = (e) => {
    e.target.classList.add("filled");
  };

  const handleInputBlur = (e) => {
    if (!e.target.value) e.target.classList.remove("filled");
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = "Full name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }
    if (!formData.jobInterest) newErrors.jobInterest = "Job interest is required";
    if (!formData.workPreference) newErrors.workPreference = "Work preference is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatBookingRange = () => {
    if (!selectedDate || !selectedSlot) return { dateStr: "", timeRange: "", weekday: "" };
    const start = new Date(selectedDate);
    start.setHours(selectedSlot.h, selectedSlot.m, 0, 0);
    const end = new Date(start.getTime() + MEETING_DURATION_MIN * 60 * 1000);
    const weekday = start.toLocaleDateString("en-US", { weekday: "long" });
    const dateStr = start.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    const opt = { hour: "numeric", minute: "2-digit", hour12: true };
    const timeRange = `${start.toLocaleTimeString("en-US", opt).replace(":00 ", " ")} – ${end
      .toLocaleTimeString("en-US", opt)
      .replace(":00 ", " ")}`;
    return { dateStr, timeRange, weekday, start };
  };

  const handleSubmitDetails = async () => {
    if (!validate()) return;
    if (!selectedDate || !selectedSlot || !isSelectableDay(selectedDate)) {
      alert("Please choose a date and time in the available calendar range.");
      setStep("schedule");
      return;
    }
    const sessionId = localStorage.getItem("session_id");
    if (!sessionId) {
      navigate("/");
      return;
    }

    const { dateStr, timeRange, weekday, start } = formatBookingRange();
    const eventTitle =
      formData.jobInterest && formData.jobInterest !== "Other"
        ? formData.jobInterest
        : "30 Minute Meeting";

    try {
      const res = await fetch(`${API_URL}/api/booking`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId,
          email: formData.email.trim(),
          fullName: formData.fullName.trim(),
          role: formData.jobInterest,
          workPreference: formData.workPreference,
          time: timeRange || "To be scheduled",
          date: dateStr || "To be scheduled",
        }),
      });
      if (!res.ok) console.error("Failed to send booking data");
      const summary = {
        email: formData.email.trim(),
        eventTitle,
        dayNum: start ? start.getDate() : "",
        monthShort: start ? start.toLocaleDateString("en-US", { month: "short" }) : "",
        weekdayLine: weekday && timeRange ? `${weekday} · ${timeRange}` : timeRange,
        timezoneLine: start ? formatTimezoneLabel(start) : formatTimezoneLabel(new Date()),
      };
      localStorage.setItem(`booking_summary_${sessionId}`, JSON.stringify(summary));
      localStorage.setItem(`confirmation_done_${sessionId}`, "1");
      setConfirmedSummary(summary);
      setStep("confirmed");
    } catch (e) {
      console.error(e);
      alert("Something went wrong. Please try again.");
    }
  };

  const goScheduleNext = () => {
    if (!selectedDate || !selectedSlot || !isSelectableDay(selectedDate)) {
      alert("Please select a date and time in the available calendar range.");
      return;
    }
    setStep("details");
  };

  const monthTitle = viewMonthStart.toLocaleDateString("en-US", { month: "long", year: "numeric" });

  return (
    <div className="calendy-div cc-schedule-page epic-schedule-page">
      <div className="calendy-grid cc-schedule-card">
        <div className="cc-schedule-left">
          <div className="cc-schedule-left-bg" aria-hidden="true">
            <div className="cc-epic-geo cc-epic-geo-1" />
            <div className="cc-epic-geo cc-epic-geo-2" />
            <div className="cc-epic-geo cc-epic-geo-3" />
            <div className="cc-epic-geo cc-epic-geo-4" />
          </div>
          <div className="cc-schedule-profile">
            <div className="cc-schedule-logo-bar">
              <img src={companyLogo} alt="Epic Games" className="cc-schedule-logo" />
            </div>

            <div className="cc-schedule-avatar-wrap">
              <img src={agentAvatar} alt="Engie DeVora" className="cc-schedule-avatar" />
              <span className="cc-schedule-status" aria-hidden="true" />
            </div>

            <h3 className="cc-schedule-name">Engie DeVora</h3>
            <p className="cc-schedule-title">Talent Acquisition Manager</p>
            <p className="cc-schedule-company">
              <IconBrand />
              <span>Epic Games</span>
            </p>
            <p className="cc-schedule-tagline">Create the Future of Interactive Entertainment</p>
          </div>

          <div className="cc-schedule-divider" />

          <div className="cc-schedule-details">
            <div className="cc-schedule-detail-row">
              <div className="cc-schedule-icon-circle">
                <IconStopwatch />
              </div>
              <div className="cc-schedule-detail-text">
                <strong>30 Minute Meeting</strong>
                <span>30 min</span>
              </div>
            </div>
            <div className="cc-schedule-detail-row">
              <div className="cc-schedule-icon-circle">
                <IconVideo />
              </div>
              <div className="cc-schedule-detail-text">
                <strong>Web Conferencing details</strong>
                <span>Provide upon confirmation</span>
              </div>
            </div>
          </div>

          <div className="cc-schedule-wave-footer">
            <img src={epicWave} alt="" className="cc-schedule-wave" aria-hidden="true" />
            <div className="cc-schedule-footer-links">
              <a href="#cookie-settings">Cookie settings</a>
              <span className="cc-schedule-footer-sep">|</span>
              <a href="#privacy">Privacy Policy</a>
            </div>
          </div>
        </div>

        <div className="right-side cc-confirmation-right">
          <div className="cc-gcal-shell">
            <div className="cc-gcal-header">
              <div className="gcal-wordmark-wrap">
                <GoogleWorkspaceWordmark className="gcal-wordmark-svg" aria-label="Google Calendar" role="img" />
              </div>
            </div>

            <div className="cc-gcal-body">
          {step === "schedule" && (
            <div className="step-content schedule-step">
              <h2>Select date and time</h2>
              <div className="calendar-container">
                <div className="calendar-header">
                  <button
                    type="button"
                    className="month-nav"
                    aria-label="Previous month"
                    disabled={!canGoPrev}
                    onClick={() =>
                      setViewMonth((v) => {
                        const s = startOfMonth(v);
                        return new Date(s.getFullYear(), s.getMonth() - 1, 1);
                      })
                    }
                  >
                    ‹
                  </button>
                  <h3>{monthTitle}</h3>
                  <button
                    type="button"
                    className="month-nav"
                    aria-label="Next month"
                    disabled={!canGoNext}
                    onClick={() =>
                      setViewMonth((v) => {
                        const s = startOfMonth(v);
                        return new Date(s.getFullYear(), s.getMonth() + 1, 1);
                      })
                    }
                  >
                    ›
                  </button>
                </div>
                <div className="calendar-grid">
                  <div className="calendar-weekdays">
                    {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((w) => (
                      <div key={w}>{w}</div>
                    ))}
                  </div>
                  <div className="calendar-days">
                    {monthCells.map((cell, idx) => {
                      if (!cell) {
                        return <div key={`e-${idx}`} className="calendar-day empty" />;
                      }
                      const sel =
                        selectedDate &&
                        startOfDay(cell).getTime() === startOfDay(selectedDate).getTime();
                      const dis = !isSelectableDay(cell);
                      return (
                        <button
                          key={cell.toISOString()}
                          type="button"
                          className={`calendar-day${sel ? " selected" : ""}${dis ? " disabled" : ""}`}
                          disabled={dis}
                          onClick={() => {
                            setSelectedDate(cell);
                            setSelectedSlot(null);
                          }}
                        >
                          {cell.getDate()}
                          {!dis && <span className="availability-dot" aria-hidden />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="time-slots-container">
                <div className="time-slots-header">Available times</div>
                <div className="time-slots-grid">
                  {selectedDate ? (
                    timeSlots.map((slot) => {
                      const active =
                        selectedSlot &&
                        selectedSlot.h === slot.h &&
                        selectedSlot.m === slot.m;
                      return (
                        <button
                          key={`${slot.h}-${slot.m}`}
                          type="button"
                          className={`time-slot${active ? " selected" : ""}`}
                          onClick={() => setSelectedSlot(slot)}
                        >
                          {slot.label}
                        </button>
                      );
                    })
                  ) : (
                    <p style={{ gridColumn: "1 / -1", color: "#666", fontSize: 14 }}>
                      Choose a date to see times.
                    </p>
                  )}
                </div>
              </div>
              <button type="button" className="continue-btn" onClick={goScheduleNext}>
                Continue
              </button>
            </div>
          )}

          {step === "details" && (
            <div className="step-content">
              <h2>Enter details</h2>

              <div className="form-group">
                <div className="input-wrapper">
                  <input
                    type="text"
                    id="cf-fullName"
                    className={`${errors.fullName ? "error-input" : ""} ${formData.fullName ? "filled" : ""}`}
                    value={formData.fullName}
                    onChange={(e) => handleInputChange("fullName", e.target.value)}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    placeholder=" "
                  />
                  <label htmlFor="cf-fullName" className={errors.fullName ? "error-label" : ""}>
                    Full Name *
                  </label>
                </div>
                {errors.fullName && <div className="error-message">{errors.fullName}</div>}
              </div>

              <div className="form-group">
                <div className="input-wrapper">
                  <input
                    type="email"
                    id="cf-email"
                    className={`${errors.email ? "error-input" : ""} ${formData.email ? "filled" : ""}`}
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    placeholder=" "
                  />
                  <label htmlFor="cf-email" className={errors.email ? "error-label" : ""}>
                    Email *
                  </label>
                </div>
                {errors.email && <div className="error-message">{errors.email}</div>}
              </div>

              <div className="form-group">
                <div className="select-wrapper">
                  <select
                    id="cf-jobInterest"
                    className={`${errors.jobInterest ? "error-input" : ""} ${formData.jobInterest ? "filled" : ""}`}
                    value={formData.jobInterest}
                    onChange={(e) => handleInputChange("jobInterest", e.target.value)}
                  >
                    <option value="">Select your job interest</option>
                    {JOB_INTERESTS.map((interest) => (
                      <option key={interest} value={interest}>
                        {interest}
                      </option>
                    ))}
                  </select>
                  <label htmlFor="cf-jobInterest" className={errors.jobInterest ? "error-label" : ""}>
                    Job Interest *
                  </label>
                </div>
                {errors.jobInterest && <div className="error-message">{errors.jobInterest}</div>}
              </div>

              <div className="form-group">
                <div className="select-wrapper">
                  <select
                    id="cf-workPreference"
                    className={`${errors.workPreference ? "error-input" : ""} ${formData.workPreference ? "filled" : ""}`}
                    value={formData.workPreference}
                    onChange={(e) => handleInputChange("workPreference", e.target.value)}
                  >
                    <option value="">Select work preference</option>
                    {WORK_PREFERENCES.map((pref) => (
                      <option key={pref} value={pref}>
                        {pref}
                      </option>
                    ))}
                  </select>
                  <label htmlFor="cf-workPreference" className={errors.workPreference ? "error-label" : ""}>
                    Work Preference *
                  </label>
                </div>
                {errors.workPreference && <div className="error-message">{errors.workPreference}</div>}
              </div>

              <div className="schedule-actions">
                <button type="button" className="back-btn" onClick={() => setStep("schedule")}>
                  Back
                </button>
                <button type="button" className="continue-btn" onClick={handleSubmitDetails}>
                  Schedule event
                </button>
              </div>
            </div>
          )}

          {step === "confirmed" && (
            <div className="gcal-booking-confirmed">
              <div className="gcal-bc-checkwrap" aria-hidden="true">
                <svg className="gcal-bc-check" width="48" height="48" viewBox="0 0 24 24" focusable="false">
                  <path
                    fill="#188038"
                    d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
                  />
                </svg>
              </div>
              <h1 className="gcal-bc-title">Booking confirmed</h1>
              <p className="gcal-bc-email-line">
                Email sent to
                <br />
                <span className="gcal-bc-email">{confirmedSummary?.email || "—"}</span>
              </p>
              <div className="gcal-bc-hint">
                Can’t find the event on your calendar?
                <div className="gcal-bc-hint-sub">Check your email and confirm the invitation</div>
              </div>
              <hr className="gcal-bc-rule" />
              <div className="gcal-bc-event-row">
                <div className="gcal-bc-datebox">
                  <div className="gcal-bc-daynum">{confirmedSummary?.dayNum ?? "—"}</div>
                  <div className="gcal-bc-monthshort">{confirmedSummary?.monthShort ?? ""}</div>
                </div>
                <div className="gcal-bc-event-meta">
                  <div className="gcal-bc-event-title">{confirmedSummary?.eventTitle || "(No title)"}</div>
                  <div className="gcal-bc-event-time">{confirmedSummary?.weekdayLine || ""}</div>
                  <div className="gcal-bc-event-tz">{confirmedSummary?.timezoneLine || ""}</div>
                </div>
              </div>
              <hr className="gcal-bc-rule" />
              <div className="gcal-bc-change">
                Need to change?
                <br />
                <button type="button" className="gcal-bc-linkbtn" onClick={() => navigate("/")}>
                  Cancel your appointment
                </button>
              </div>
              <div className="gcal-bc-close-wrap">
                <button type="button" className="gcal-bc-close" onClick={() => navigate("/")}>
                  Close
                </button>
              </div>
            </div>
          )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
